import { ChangeEvent, FC, useEffect, useState } from 'react';
import { useSetAtom } from 'jotai';
import { SidePanel } from '@carbon/ibm-products';
import { InlineNotification, TextInput } from '@carbon/react';
import { editLineItemAtom, lineItemDetailUrl } from './atoms/line-items';
import type { LineItemRow } from './types-line-item';
import styles from 'pages/afe-catalog/structuring/identification.module.scss';

interface Props {
  open: boolean;
  documentId: number | string;
  row: LineItemRow;
  onClose: () => void;
  onSaved: () => void;
}

// key = camelCase row field (seed source), requestKey = snake_case body key (the
// controller's permit list). kind drives the input type; numbers are sent as
// strings and coerced server-side (AR numeric cast).
interface FieldDef {
  key: keyof LineItemRow;
  label: string;
  requestKey: string;
  kind: 'text' | 'number';
}

const FIELDS: FieldDef[] = [
  {
    key: 'interestType',
    label: 'Type',
    requestKey: 'interest_type',
    kind: 'text',
  },
  {
    key: 'adjustmentCode',
    label: 'Adjustment Code',
    requestKey: 'adjustment_code',
    kind: 'text',
  },
  {
    key: 'lineItemDecimal',
    label: 'Line Item Decimal',
    requestKey: 'line_item_decimal',
    kind: 'number',
  },
  { key: 'price', label: 'Price', requestKey: 'price', kind: 'number' },
  {
    key: 'grossVolume',
    label: 'Gross Volume',
    requestKey: 'gross_volume',
    kind: 'number',
  },
  {
    key: 'grossValue',
    label: 'Gross Value',
    requestKey: 'gross_value',
    kind: 'number',
  },
  {
    key: 'grossTaxes',
    label: 'Gross Taxes',
    requestKey: 'gross_taxes',
    kind: 'number',
  },
  {
    key: 'grossAdjustments',
    label: 'Gross Adjustments',
    requestKey: 'gross_adjustments',
    kind: 'number',
  },
  {
    key: 'grossNet',
    label: 'Gross Net',
    requestKey: 'gross_net',
    kind: 'number',
  },
  {
    key: 'ownerVolume',
    label: 'Owner Volume',
    requestKey: 'owner_volume',
    kind: 'number',
  },
  {
    key: 'ownerValue',
    label: 'Owner Value',
    requestKey: 'owner_value',
    kind: 'number',
  },
  {
    key: 'ownerTaxes',
    label: 'Owner Taxes',
    requestKey: 'owner_taxes',
    kind: 'number',
  },
  {
    key: 'ownerAdjustments',
    label: 'Owner Adjustments',
    requestKey: 'owner_adjustments',
    kind: 'number',
  },
  {
    key: 'lineItemNetValue',
    label: 'Line Item Net Value',
    requestKey: 'line_item_net_value',
    kind: 'number',
  },
];

const blank = (value: string): string | null => {
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
};

// Edit one royalty line item in place. Mirror of the Overview edit panel: a flat
// working copy submitted as a snake_case { line_item: {...} } body. Editing any
// value recomputes the variance indicators on the next list load (FE-derived).
export const LineItemEditPanel: FC<Props> = ({
  open,
  documentId,
  row,
  onClose,
  onSaved,
}) => {
  const editLineItem = useSetAtom(editLineItemAtom);
  const [values, setValues] = useState<Record<string, string>>({});
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setError(null);
      return;
    }
    const seeded: Record<string, string> = {};
    FIELDS.forEach((def) => {
      const raw = row[def.key];
      seeded[def.key] = raw === null || raw === undefined ? '' : String(raw);
    });
    setValues(seeded);
  }, [open, row]);

  const setValue = (key: string, value: string) =>
    setValues((prev) => ({ ...prev, [key]: value }));

  const handleSave = () => {
    // Submit only fields that actually changed vs the seeded baseline. The
    // backend records a LineItemEdited event on every update, so sending the
    // full set would flip an untouched line to 'edited' and log all columns as
    // changed for a single-field edit.
    const lineItem: Record<string, string | null> = {};
    FIELDS.forEach((def) => {
      const raw = row[def.key];
      const seeded = raw === null || raw === undefined ? '' : String(raw);
      const current = values[def.key] ?? '';
      if (current === seeded) return;
      lineItem[def.requestKey] = blank(current);
    });

    // No-op edit → close without firing an edit event.
    if (Object.keys(lineItem).length === 0) {
      onClose();
      return;
    }

    setPending(true);
    setError(null);

    editLineItem({
      url: lineItemDetailUrl(documentId, row.id),
      method: 'PATCH',
      data: { line_item: lineItem },
      onSuccess: () => {
        setPending(false);
        onSaved();
      },
      onError: (m) => {
        setPending(false);
        setError(m ?? 'Failed to save line item.');
      },
    });
  };

  return (
    <SidePanel
      open={open}
      onRequestClose={onClose}
      title="Edit Line Item"
      subtitle={row.propertyName ?? row.propertyNumber ?? undefined}
      slideIn
      selectorPageContent=".rc-structuring-shell"
      placement="right"
      size="md"
      actions={[
        { kind: 'secondary' as const, label: 'Cancel', onClick: onClose },
        {
          kind: 'primary' as const,
          label: pending ? 'Saving…' : 'Save',
          disabled: pending,
          onClick: handleSave,
        },
      ]}
    >
      <div className={`${styles.panelForm} ${styles.panelLightFields}`}>
        {error && (
          <InlineNotification
            kind="error"
            lowContrast
            title="Error"
            subtitle={error}
            onCloseButtonClick={() => setError(null)}
          />
        )}
        <div className={styles.panelColumn}>
          {FIELDS.map((def) => (
            <TextInput
              key={def.key}
              id={`rc-line-item-${def.key}`}
              labelText={def.label}
              type={def.kind === 'number' ? 'number' : 'text'}
              value={values[def.key] ?? ''}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setValue(def.key, e.target.value)
              }
            />
          ))}
        </div>
      </div>
    </SidePanel>
  );
};
